import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, UserPlus } from 'lucide-react';
import { pricingData } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const planOrder = [
  'monthly',
  'trimestralFirstYear',
  'veteran',
  'classesOnly',
  'oneClassWeekly',
  'family3',
  'family4',
  'pack6months',
  'pack12months',
  'pack5',
  'singleEntry',
];

const planOptions = planOrder.map((key) => {
  const p = pricingData[key];
  return `${p.description} - ${p.price}`;
});

export const JoinModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', plan: '', message: '' });
  const [loading, setLoading] = useState(false);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.plan) {
      toast.error('Omple tots els camps obligatoris');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/members`, form);
      toast.success('Sol·licitud enviada! Ens posarem en contacte aviat.');
      setForm({ name: '', email: '', phone: '', plan: '', message: '' });
      onOpenChange(false);
    } catch (err) {
      toast.error('Hi ha hagut un error. Torna-ho a provar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-2 border-[#39FF14]/40 text-white max-w-lg" data-testid="join-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-[#39FF14]" />
            Fes-te <span className="text-[#39FF14]">Soci</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Omple el formulari i ens posarem en contacte amb tu.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="join-name" className="text-gray-300">Nom i cognoms *</Label>
            <Input
              id="join-name"
              data-testid="join-name-input"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              className="bg-gray-800 border-gray-700 text-white mt-1"
              placeholder="El teu nom"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="join-email" className="text-gray-300">Correu electrònic *</Label>
              <Input
                id="join-email"
                type="email"
                data-testid="join-email-input"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white mt-1"
                placeholder="tu@correu.com"
              />
            </div>
            <div>
              <Label htmlFor="join-phone" className="text-gray-300">Telèfon *</Label>
              <Input
                id="join-phone"
                data-testid="join-phone-input"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white mt-1"
                placeholder="600 000 000"
              />
            </div>
          </div>
          <div>
            <Label className="text-gray-300">Quota d'interès *</Label>
            <Select value={form.plan} onValueChange={(v) => update('plan', v)}>
              <SelectTrigger data-testid="join-plan-select" className="bg-gray-800 border-gray-700 text-white mt-1">
                <SelectValue placeholder="Selecciona una quota" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 text-white">
                {planOptions.map((opt) => (
                  <SelectItem key={opt} value={opt} className="focus:bg-[#39FF14]/20">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="join-message" className="text-gray-300">Missatge (opcional)</Label>
            <Textarea
              id="join-message"
              data-testid="join-message-input"
              value={form.message}
              onChange={(e) => update('message', e.target.value)}
              className="bg-gray-800 border-gray-700 text-white mt-1"
              placeholder="Alguna cosa que vulguis dir-nos?"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="join-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#2EDD0F] text-black font-bold px-6 py-3 rounded-full transition-all transform hover:scale-105 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
            {loading ? 'Enviant...' : 'Enviar sol·licitud'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
